import { useState, useEffect, useCallback, useRef } from 'react';
import { axiosInstance } from '@/App';
import { toast } from 'sonner';

// Pipeline stage order and display config
const PIPELINE_STAGES = ['trial_scheduled', 'trial_done', 'follow_up', 'closed_won', 'closed_lost'];
const TERMINAL_STAGES = ['closed_won', 'closed_lost'];

// Next stage for swipe-right advance (non-terminal only)
const NEXT_STAGE_MAP = {
  trial_scheduled: 'trial_done',
  trial_done: 'follow_up',
  follow_up: 'closed_won',
};

export { PIPELINE_STAGES, TERMINAL_STAGES, NEXT_STAGE_MAP };

/**
 * useFMPipeline - fetches pipeline entries for a gym with grouping, summary, and renewals-due.
 *
 * @param {string} gymId - selected gym ID
 * @param {string|null} type - optional filter: "fresh", "renewal", or null for all
 * @param {string|null} stage - optional stage filter
 *
 * Returns { entries, byStage, summary, renewalsDue, loading, error, refetch, advanceEntry, markLost, createEntry }
 */
export function useFMPipeline(gymId, type = null, stage = null) {
  const [entries, setEntries] = useState([]);
  const [byStage, setByStage] = useState({});
  const [summary, setSummary] = useState({ total_pipeline_value: 0, total_entries: 0, won_value: 0, won_count: 0 });
  const [renewalsDue, setRenewalsDue] = useState({ members: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const abortRef = useRef(null);

  const fetchPipeline = useCallback(async () => {
    if (!gymId) {
      setEntries([]);
      setByStage({});
      setLoading(false);
      return;
    }

    if (abortRef.current) {
      abortRef.current.abort();
    }
    const controller = new AbortController();
    abortRef.current = controller;

    try {
      setLoading(true);
      setError(null);

      const params = { gym_id: gymId };
      if (type) params.type = type;
      if (stage) params.stage = stage;

      const [pipelineRes, renewalsRes] = await Promise.allSettled([
        axiosInstance.get('/fm/pipeline', { params, signal: controller.signal }),
        axiosInstance.get('/fm/pipeline/renewals-due', { params: { gym_id: gymId }, signal: controller.signal }),
      ]);

      if (pipelineRes.status === 'fulfilled') {
        const data = pipelineRes.value.data;
        setEntries(data.entries || []);
        setByStage(data.by_stage || {});
        setSummary(data.summary || { total_pipeline_value: 0, total_entries: 0, won_value: 0, won_count: 0 });
      } else {
        throw pipelineRes.reason;
      }

      if (renewalsRes.status === 'fulfilled') {
        setRenewalsDue(renewalsRes.value.data || { members: [], total: 0 });
      }
    } catch (err) {
      if (err?.name === 'CanceledError' || err?.name === 'AbortError') return;
      console.error('Failed to fetch pipeline:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to load pipeline data');
    } finally {
      setLoading(false);
    }
  }, [gymId, type, stage]);

  useEffect(() => {
    fetchPipeline();
    return () => {
      if (abortRef.current) abortRef.current.abort();
    };
  }, [fetchPipeline]);

  /**
   * advanceEntry - advance pipeline entry to target stage with optimistic update.
   */
  const advanceEntry = useCallback(async (entryId, targetStage) => {
    // Optimistic update
    setEntries((prev) =>
      prev.map((e) => (e.entry_id === entryId ? { ...e, stage: targetStage } : e))
    );
    // Rebuild byStage optimistically
    setByStage((prev) => {
      const updated = {};
      for (const s of PIPELINE_STAGES) {
        updated[s] = (prev[s] || []).filter((e) => e.entry_id !== entryId);
      }
      // Find the entry from current entries and move it
      const movedEntry = entries.find((e) => e.entry_id === entryId);
      if (movedEntry && updated[targetStage]) {
        updated[targetStage] = [{ ...movedEntry, stage: targetStage }, ...updated[targetStage]];
      }
      return updated;
    });

    try {
      await axiosInstance.put(`/fm/pipeline/${entryId}/advance`, { target_stage: targetStage });
      toast.success(targetStage === 'closed_won' ? 'Deal closed!' : 'Stage advanced');
      fetchPipeline();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to advance entry');
      fetchPipeline();
    }
  }, [entries, fetchPipeline]);

  /**
   * markLost - mark pipeline entry as closed_lost.
   */
  const markLost = useCallback(async (entryId) => {
    // Optimistic update
    setEntries((prev) =>
      prev.map((e) => (e.entry_id === entryId ? { ...e, stage: 'closed_lost' } : e))
    );

    try {
      await axiosInstance.put(`/fm/pipeline/${entryId}/advance`, { target_stage: 'closed_lost' });
      toast.success('Marked as lost');
      fetchPipeline();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to mark as lost');
      fetchPipeline();
    }
  }, [fetchPipeline]);

  /**
   * createEntry - create new pipeline entry.
   */
  const createEntry = useCallback(async (data) => {
    try {
      await axiosInstance.post('/fm/pipeline', data);
      toast.success('Lead added to pipeline');
      fetchPipeline();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create pipeline entry');
      throw err;
    }
  }, [fetchPipeline]);

  return {
    entries,
    byStage,
    summary,
    renewalsDue,
    loading,
    error,
    refetch: fetchPipeline,
    advanceEntry,
    markLost,
    createEntry,
  };
}
